import {
  Building,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  MapPin,
  User,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import logo from "../../assets/logo.png";
import { validateCpf } from "../../utils/validateCpf";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useLoginContext } from "../../context/login.context";
import { login } from "../../services/auth.service";
import { getAllCategories } from "../../services/category.service";
import { signupApiRequest } from "../../services/signup.service";

const steps = [
  { id: 0, title: "Cadastro de Usuário", icon: User },
  { id: 1, title: "Endereço", icon: MapPin },
  { id: 2, title: "Cadastro da sua Marca", icon: Building },
  { id: 3, title: "Confirmação do Cadastro", icon: CheckCircle },
];

interface UserFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone: string;
  birthday: string;
  zipcode: string;
  city: string;
  state: string;
  district: string;
  address: string;
  address_number: string;
  address_complement: string;
  cpf: string;
  cnpj: string;
}

interface BrandFormData {
  brandName: string;
  description: string;
  category: string;
  website: string;
  instagram: string;
  brandItems: string;
}

const SuccessMessage = () => (
  <Alert className="border-blue-200 bg-green-50">
    <CheckCircle className="h-4 w-4 text-blue-600" />
    <AlertDescription className="text-blue-800">
      <strong>Parabéns, seu perfil na Praceria já está no ar!</strong>
      <br />
      Agora é só acessar sua conta e se pré-inscrever para a próxima edição do
      evento — te esperamos lá!
      <br />
      <em>
        Ah, e um toque importante: dá uma olhadinha no spam ou na lixeira do seu
        e-mail. Às vezes nossas mensagens se escondem por lá.
      </em>
    </AlertDescription>
  </Alert>
);

const ErrorMessage = ({ message }: { message?: string }) => (
  <Alert className="border-red-200 bg-red-50">
    <AlertDescription className="text-red-800">
      <strong>Ocorreu algum erro na sua inscrição na Praceria!</strong>
      <br />
      Recarregue a página e tente novamente!
      {message && (
        <>
          <br />
          <em>Erro: {message}</em>
        </>
      )}
    </AlertDescription>
  </Alert>
);

const AddressForm = ({
  formData,
  setFormData,
  errors,
}: {
  formData: UserFormData;
  setFormData: React.Dispatch<React.SetStateAction<UserFormData>>;
  errors: Record<string, string>;
}) => {
  const [isLoadingCep, setIsLoadingCep] = useState(false);
  const [cepError, setCepError] = useState<string | null>(null);

  const formatZipcode = (value: string) => {
    const cleaned = value.replace(/\D/g, "");

    if (cleaned.length <= 5) {
      return cleaned;
    }

    return `${cleaned.slice(0, 5)}-${cleaned.slice(5, 8)}`;
  };

  const searchCep = async (cep: string) => {
    const cleanCep = cep.replace(/\D/g, "");

    if (cleanCep.length !== 8) {
      return;
    }

    setIsLoadingCep(true);
    setCepError(null);

    try {
      const response = await fetch(
        `https://viacep.com.br/ws/${cleanCep}/json/`,
      );
      const data = await response.json();

      if (data.erro) {
        setCepError("CEP não encontrado");
        return;
      }

      // Preenche automaticamente os campos de endereço, preservando o CEP formatado
      setFormData((prevFormData: UserFormData) => ({
        ...prevFormData,
        city: data.localidade || prevFormData.city,
        state: data.uf || prevFormData.state,
        district: data.bairro || prevFormData.district,
        address: data.logradouro || prevFormData.address,
      }));
    } catch (error) {
      setCepError("Erro ao buscar CEP. Tente novamente.");
    } finally {
      setIsLoadingCep(false);
    }
  };

  const handleCepChange = (value: string) => {
    const formatted = formatZipcode(value);
    setFormData({ ...formData, zipcode: formatted });
    setCepError(null);

    // Busca automaticamente quando o CEP estiver completo
    const cleanCep = formatted.replace(/\D/g, "");
    if (cleanCep.length === 8) {
      searchCep(cleanCep);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label
          htmlFor="zipcode"
          className="text-base font-medium text-gray-700"
        >
          CEP
        </Label>
        <Input
          id="zipcode"
          type="text"
          placeholder="00000-000"
          value={formData.zipcode}
          onChange={(e) => handleCepChange(e.target.value)}
          className="h-11 border-gray-200 focus:border-[#24324D] focus:ring-[#24324D]"
          maxLength={9}
          required
        />
        {isLoadingCep && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#24324D]"></div>
          </div>
        )}
        {errors.zipcode && (
          <p className="text-base text-red-600">{errors.zipcode}</p>
        )}
        {cepError && <p className="text-base text-red-600">{cepError}</p>}
        {isLoadingCep && (
          <p className="text-base text-blue-600">Buscando endereço...</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="city" className="text-base font-medium text-gray-700">
            Cidade
          </Label>
          <Input
            id="city"
            type="text"
            placeholder="Sua cidade"
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            className="h-11 border-gray-200 focus:border-[#24324D] focus:ring-[#24324D]"
            required
          />
          {errors.city && (
            <p className="text-base text-red-600">{errors.city}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label
            htmlFor="state"
            className="text-base font-medium text-gray-700"
          >
            Estado
          </Label>
          <Input
            id="state"
            type="text"
            placeholder="Seu estado"
            value={formData.state}
            onChange={(e) =>
              setFormData({ ...formData, state: e.target.value })
            }
            className="h-11 border-gray-200 focus:border-[#24324D] focus:ring-[#24324D]"
            required
          />
          {errors.state && (
            <p className="text-base text-red-600">{errors.state}</p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label
          htmlFor="district"
          className="text-base font-medium text-gray-700"
        >
          Bairro
        </Label>
        <Input
          id="district"
          type="text"
          placeholder="Seu bairro"
          value={formData.district}
          onChange={(e) =>
            setFormData({ ...formData, district: e.target.value })
          }
          className="h-11 border-gray-200 focus:border-[#24324D] focus:ring-[#24324D]"
          required
        />
        {errors.district && (
          <p className="text-base text-red-600">{errors.district}</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2 space-y-2">
          <Label
            htmlFor="address"
            className="text-base font-medium text-gray-700"
          >
            Endereço
          </Label>
          <Input
            id="address"
            type="text"
            placeholder="Rua, avenida, etc."
            value={formData.address}
            onChange={(e) =>
              setFormData({ ...formData, address: e.target.value })
            }
            className="h-11 border-gray-200 focus:border-[#24324D] focus:ring-[#24324D]"
            required
          />
          {errors.address && (
            <p className="text-base text-red-600">{errors.address}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label
            htmlFor="address_number"
            className="text-base font-medium text-gray-700"
          >
            Número
          </Label>
          <Input
            id="address_number"
            type="text"
            placeholder="123"
            value={formData.address_number}
            onChange={(e) =>
              setFormData({ ...formData, address_number: e.target.value })
            }
            className="h-11 border-gray-200 focus:border-[#24324D] focus:ring-[#24324D]"
            required
          />
          {errors.address_number && (
            <p className="text-base text-red-600">{errors.address_number}</p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label
          htmlFor="address_complement"
          className="text-base font-medium text-gray-700"
        >
          Complemento (opcional)
        </Label>
        <Input
          id="address_complement"
          type="text"
          placeholder="Apartamento, bloco, etc."
          value={formData.address_complement}
          onChange={(e) =>
            setFormData({ ...formData, address_complement: e.target.value })
          }
          className="h-11 border-gray-200 focus:border-[#24324D] focus:ring-[#24324D]"
        />
        {errors.address_complement && (
          <p className="text-base text-red-600">{errors.address_complement}</p>
        )}
      </div>
    </div>
  );
};

const UserForm = ({
  formData,
  setFormData,
  errors,
}: {
  formData: UserFormData;
  setFormData: (data: UserFormData) => void;
  errors: Record<string, string>;
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // Função para formatar telefone
  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 7)
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(
      7,
      11,
    )}`;
  };

  // Função para formatar CPF
  const formatCpf = (value: string) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6)
      return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
    if (numbers.length <= 9)
      return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(
        6,
      )}`;
    return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(
      6,
      9,
    )}-${numbers.slice(9, 11)}`;
  };

  // Função para formatar CNPJ
  const formatCnpj = (value: string) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 5)
      return `${numbers.slice(0, 2)}.${numbers.slice(2)}`;
    if (numbers.length <= 8)
      return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(
        5,
      )}`;
    if (numbers.length <= 12)
      return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(
        5,
        8,
      )}/${numbers.slice(8)}`;
    return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(
      5,
      8,
    )}/${numbers.slice(8, 12)}-${numbers.slice(12, 14)}`;
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name" className="text-base font-medium text-gray-700">
          Nome Completo
        </Label>
        <Input
          id="name"
          type="text"
          placeholder="Seu nome completo"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="h-11 border-gray-200 focus:border-[#24324D] focus:ring-[#24324D]"
          required
        />
        {errors.name && <p className="text-base text-red-600">{errors.name}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="email" className="text-base font-medium text-gray-700">
          Email
        </Label>
        <Input
          id="email"
          type="email"
          placeholder="seu@email.com"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="h-11 border-gray-200 focus:border-[#24324D] focus:ring-[#24324D]"
          required
        />
        {errors.email && (
          <p className="text-base text-red-600">{errors.email}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone" className="text-base font-medium text-gray-700">
          Telefone celular com DDD
        </Label>
        <Input
          id="phone"
          type="tel"
          placeholder="(11) 99999-9999"
          value={formData.phone}
          onChange={(e) => {
            const formatted = formatPhone(e.target.value);
            setFormData({ ...formData, phone: formatted });
          }}
          className="h-11 border-gray-200 focus:border-[#24324D] focus:ring-[#24324D]"
          maxLength={15}
          required
        />
        {errors.phone && (
          <p className="text-base text-red-600">{errors.phone}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label
          htmlFor="birthday"
          className="text-base font-medium text-gray-700"
        >
          Data de Nascimento
        </Label>
        <Input
          id="birthday"
          type="date"
          value={formData.birthday}
          onChange={(e) =>
            setFormData({ ...formData, birthday: e.target.value })
          }
          className="h-11 border-gray-200 focus:border-[#24324D] focus:ring-[#24324D]"
          required
        />
        {errors.birthday && (
          <p className="text-base text-red-600">{errors.birthday}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="cpf" className="text-base font-medium text-gray-700">
          CPF
        </Label>
        <Input
          id="cpf"
          type="text"
          placeholder="000.000.000-00"
          value={formData.cpf}
          onChange={(e) => {
            const formatted = formatCpf(e.target.value);
            setFormData({ ...formData, cpf: formatted });
          }}
          className="h-11 border-gray-200 focus:border-[#24324D] focus:ring-[#24324D]"
          maxLength={14}
          required
        />
        {errors.cpf && <p className="text-base text-red-600">{errors.cpf}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="cnpj" className="text-base font-medium text-gray-700">
          CNPJ (opcional)
        </Label>
        <Input
          id="cnpj"
          type="text"
          placeholder="00.000.000/0000-00"
          value={formData.cnpj}
          onChange={(e) => {
            const formatted = formatCnpj(e.target.value);
            setFormData({ ...formData, cnpj: formatted });
          }}
          className="h-11 border-gray-200 focus:border-[#24324D] focus:ring-[#24324D]"
          maxLength={18}
        />
      </div>

      <div className="space-y-2">
        <Label
          htmlFor="password"
          className="text-base font-medium text-gray-700"
        >
          Senha
        </Label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            placeholder="Sua senha (mínimo 6 caracteres)"
            value={formData.password}
            onChange={(e) =>
              setFormData({ ...formData, password: e.target.value })
            }
            className="h-11 border-gray-200 focus:border-[#24324D] focus:ring-[#24324D] pr-10"
            minLength={6}
            required
          />
          <button
            type="button"
            aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
            onClick={() => setShowPassword((v) => !v)}
            className="absolute inset-y-0 right-2 flex items-center text-gray-500 hover:text-gray-700"
          >
            {showPassword ? (
              <EyeOff className="w-5 h-5" />
            ) : (
              <Eye className="w-5 h-5" />
            )}
          </button>
        </div>
        {errors.password && (
          <p className="text-base text-red-600">{errors.password}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label
          htmlFor="confirmPassword"
          className="text-base font-medium text-gray-700"
        >
          Confirmar Senha
        </Label>
        <div className="relative">
          <Input
            id="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            placeholder="Confirme sua senha"
            value={formData.confirmPassword}
            onChange={(e) =>
              setFormData({ ...formData, confirmPassword: e.target.value })
            }
            className="h-11 border-gray-200 focus:border-[#24324D] focus:ring-[#24324D] pr-10"
            required
          />
          <button
            type="button"
            aria-label={
              showConfirmPassword
                ? "Ocultar confirmação"
                : "Mostrar confirmação"
            }
            onClick={() => setShowConfirmPassword((v) => !v)}
            className="absolute inset-y-0 right-2 flex items-center text-gray-500 hover:text-gray-700"
          >
            {showConfirmPassword ? (
              <EyeOff className="w-5 h-5" />
            ) : (
              <Eye className="w-5 h-5" />
            )}
          </button>
        </div>
        {errors.confirmPassword && (
          <p className="text-base text-red-600">{errors.confirmPassword}</p>
        )}
      </div>
    </div>
  );
};

const BrandForm = ({
  formData,
  setFormData,
  errors,
  categories,
}: {
  formData: BrandFormData;
  setFormData: (data: BrandFormData) => void;
  errors: Record<string, string>;
  categories: any[];
}) => (
  <div className="space-y-4">
    <div className="space-y-2">
      <Label
        htmlFor="brandName"
        className="text-base font-medium text-gray-700"
      >
        Nome da Marca
      </Label>
      <Input
        id="brandName"
        type="text"
        placeholder="Nome da sua marca"
        value={formData.brandName}
        onChange={(e) =>
          setFormData({ ...formData, brandName: e.target.value })
        }
        className="h-11 border-gray-200 focus:border-[#24324D] focus:ring-[#24324D]"
        required
      />
      {errors.brandName && (
        <p className="text-base text-red-600">{errors.brandName}</p>
      )}
    </div>

    <div className="space-y-2">
      <Label
        htmlFor="description"
        className="text-base font-medium text-gray-700"
      >
        Descrição da Marca
      </Label>
      <textarea
        id="description"
        placeholder="Conte-nos sobre sua marca..."
        value={formData.description}
        onChange={(e) =>
          setFormData({ ...formData, description: e.target.value })
        }
        className="min-h-[100px] w-full rounded-md border border-gray-200 px-3 py-2 text-base focus:border-[#24324D] focus:ring-[#24324D] focus:outline-none focus:ring-2 focus:ring-offset-2"
        required
      />
      {errors.description && (
        <p className="text-base text-red-600">{errors.description}</p>
      )}
    </div>

    <div className="space-y-2">
      <Label htmlFor="category" className="text-base font-medium text-gray-700">
        Categoria da Marca
      </Label>
      <Select
        value={formData.category}
        onValueChange={(value) => setFormData({ ...formData, category: value })}
      >
        <SelectTrigger className="h-11 border-gray-200 focus:border-[#24324D] focus:ring-[#24324D]">
          <SelectValue placeholder="Selecione uma categoria" />
        </SelectTrigger>
        <SelectContent>
          {categories &&
            categories
              .filter((x) => x.active)
              .map((x) => (
                <SelectItem key={x.id} value={x.id.toString()}>
                  {x.name}
                </SelectItem>
              ))}
        </SelectContent>
      </Select>
      {errors.category && (
        <p className="text-base text-red-600">{errors.category}</p>
      )}
    </div>

    <div className="space-y-2">
      <Label
        htmlFor="brandItems"
        className="text-base font-medium text-gray-700"
      >
        Itens da sua marca
      </Label>
      <textarea
        id="brandItems"
        placeholder="Liste um a um os itens que você produz e pretende levar na Praceria."
        value={formData.brandItems}
        onChange={(e) =>
          setFormData({ ...formData, brandItems: e.target.value })
        }
        className="min-h-[80px] w-full rounded-md border border-gray-200 px-3 py-2 text-base focus:border-[#24324D] focus:ring-[#24324D] focus:outline-none focus:ring-2 focus:ring-offset-2"
        required
      />
      {errors.brandItems && (
        <p className="text-base text-red-600">{errors.brandItems}</p>
      )}
    </div>

    <div className="space-y-2">
      <Label htmlFor="website" className="text-base font-medium text-gray-700">
        Website (opcional)
      </Label>
      <Input
        id="website"
        type="url"
        placeholder="https://www.suamarca.com"
        value={formData.website}
        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
        className="h-11 border-gray-200 focus:border-[#24324D] focus:ring-[#24324D]"
      />
    </div>

    <div className="space-y-2">
      <Label
        htmlFor="instagram"
        className="text-base font-medium text-gray-700"
      >
        Instagram
      </Label>
      <Input
        id="instagram"
        type="text"
        placeholder="@suamarca"
        value={formData.instagram}
        onChange={(e) => {
          let value = e.target.value;
          if (value && !value.startsWith("@")) {
            value = "@" + value;
          }
          setFormData({ ...formData, instagram: value });
        }}
        className="h-11 border-gray-200 focus:border-[#24324D] focus:ring-[#24324D]"
        required
      />
      {errors.instagram && (
        <p className="text-base text-red-600">{errors.instagram}</p>
      )}
    </div>
  </div>
);

const ConfirmationStep = ({
  userFormData,
  brandFormData,
}: {
  userFormData: UserFormData;
  brandFormData: BrandFormData;
}) => (
  <div className="space-y-6">
    <div className="text-center">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        Confirme seus dados
      </h3>
      <p className="text-gray-600">
        Revise as informações antes de finalizar seu cadastro
      </p>
    </div>

    <div className="space-y-4">
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-2">Dados Pessoais</h4>
        <div className="space-y-1 text-base text-gray-600">
          <p>
            <strong>Nome:</strong> {userFormData.name}
          </p>
          <p>
            <strong>Email:</strong> {userFormData.email}
          </p>
          <p>
            <strong>Telefone:</strong> {userFormData.phone}
          </p>
          <p>
            <strong>Data de Nascimento:</strong> {userFormData.birthday}
          </p>
          <p>
            <strong>CPF:</strong> {userFormData.cpf}
          </p>
          {userFormData.cnpj && (
            <p>
              <strong>CNPJ:</strong> {userFormData.cnpj}
            </p>
          )}
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-2">Endereço</h4>
        <div className="space-y-1 text-base text-gray-600">
          <p>
            <strong>CEP:</strong> {userFormData.zipcode}
          </p>
          <p>
            <strong>Cidade:</strong> {userFormData.city}
          </p>
          <p>
            <strong>Estado:</strong> {userFormData.state}
          </p>
          <p>
            <strong>Bairro:</strong> {userFormData.district}
          </p>
          <p>
            <strong>Endereço:</strong> {userFormData.address},{" "}
            {userFormData.address_number}
          </p>
          {userFormData.address_complement && (
            <p>
              <strong>Complemento:</strong> {userFormData.address_complement}
            </p>
          )}
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-2">Dados da Marca</h4>
        <div className="space-y-1 text-base text-gray-600">
          <p>
            <strong>Nome da Marca:</strong> {brandFormData.brandName}
          </p>
          <p>
            <strong>Categoria:</strong> {brandFormData.category}
          </p>
          <p>
            <strong>Descrição:</strong> {brandFormData.description}
          </p>
          <p>
            <strong>Itens da Marca:</strong> {brandFormData.brandItems}
          </p>
          {brandFormData.website && (
            <p>
              <strong>Website:</strong> {brandFormData.website}
            </p>
          )}
          {brandFormData.instagram && (
            <p>
              <strong>Instagram:</strong> {brandFormData.instagram}
            </p>
          )}
        </div>
      </div>
    </div>
  </div>
);

export default function Signup() {
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [categories, setCategories] = useState<any[]>([]);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loginToken } = useLoginContext();

  useEffect(() => {
    getAllCategories().then((x) =>
      setCategories(x.sort((a: any, b: any) => a.name.localeCompare(b.name))),
    );
  }, []);

  const [userFormData, setUserFormData] = useState<UserFormData>({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    birthday: "2000-01-01",
    zipcode: "",
    city: "",
    state: "",
    district: "",
    address: "",
    address_number: "",
    address_complement: "",
    cpf: "",
    cnpj: "",
  });

  const [brandFormData, setBrandFormData] = useState<BrandFormData>({
    brandName: "",
    description: "",
    category: "",
    website: "",
    instagram: "",
    brandItems: "",
  });

  const validateUserForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!userFormData.name.trim()) {
      newErrors.name = "Nome é obrigatório";
    }

    if (!userFormData.email.trim()) {
      newErrors.email = "Email é obrigatório";
    } else if (!/\S+@\S+\.\S+/.test(userFormData.email)) {
      newErrors.email = "Email inválido";
    }

    if (!userFormData.phone.trim()) {
      newErrors.phone = "Telefone é obrigatório";
    } else {
      const phoneNumbers = userFormData.phone.replace(/\D/g, "");
      if (phoneNumbers.length !== 11) {
        newErrors.phone = "Telefone deve ter 11 dígitos (DDD + número)";
      }
    }

    if (!userFormData.birthday) {
      newErrors.birthday = "Data de nascimento é obrigatória";
    }

    // Validação de CPF (obrigatório)
    if (!userFormData.cpf || !userFormData.cpf.trim()) {
      newErrors.cpf = "CPF é obrigatório";
    } else {
      const cpf = userFormData.cpf.replace(/\D/g, "");
      if (cpf.length !== 11) {
        newErrors.cpf = "CPF deve ter 11 dígitos";
      } else if (!validateCpf(cpf)) {
        newErrors.cpf = "CPF inválido";
      }
    }

    // Validação de CNPJ (opcional, mas se preenchido deve ser válido)
    if (userFormData.cnpj && userFormData.cnpj.trim()) {
      const cnpj = userFormData.cnpj.replace(/\D/g, "");
      if (cnpj.length !== 14) {
        newErrors.cnpj = "CNPJ deve ter 14 dígitos";
      }
    }

    if (!userFormData.password) {
      newErrors.password = "Senha é obrigatória";
    } else if (userFormData.password.length < 6) {
      newErrors.password = "Senha deve ter pelo menos 6 caracteres";
    }

    if (userFormData.password !== userFormData.confirmPassword) {
      newErrors.confirmPassword = "Senhas não coincidem";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateAddressForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!userFormData.zipcode.trim()) {
      newErrors.zipcode = "CEP é obrigatório";
    } else {
      const zipcodeNumbers = userFormData.zipcode.replace(/\D/g, "");
      if (zipcodeNumbers.length !== 8) {
        newErrors.zipcode = "CEP deve ter 8 dígitos";
      }
    }

    if (!userFormData.city.trim()) {
      newErrors.city = "Cidade é obrigatória";
    }

    if (!userFormData.state.trim()) {
      newErrors.state = "Estado é obrigatório";
    } else if (userFormData.state.length !== 2) {
      newErrors.state = "Estado deve ter 2 caracteres (ex: SP)";
    }

    if (!userFormData.district.trim()) {
      newErrors.district = "Bairro é obrigatório";
    }

    if (!userFormData.address.trim()) {
      newErrors.address = "Endereço é obrigatório";
    }

    if (!userFormData.address_number.trim()) {
      newErrors.address_number = "Número é obrigatório";
    }

    // Validação de complemento de endereço (máximo 255 caracteres)
    if (
      userFormData.address_complement &&
      userFormData.address_complement.length > 255
    ) {
      newErrors.address_complement =
        "Complemento deve ter no máximo 255 caracteres";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateBrandForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!brandFormData.brandName.trim()) {
      newErrors.brandName = "Nome da marca é obrigatório";
    } else if (brandFormData.brandName.length > 60) {
      newErrors.brandName = "Nome da marca deve ter no máximo 60 caracteres";
    }

    if (!brandFormData.description.trim()) {
      newErrors.description = "Descrição é obrigatória";
    }

    if (!brandFormData.category.trim()) {
      newErrors.category = "Categoria é obrigatória";
    } else if (isNaN(Number(brandFormData.category))) {
      newErrors.category = "Categoria deve ser um valor válido";
    }

    if (!brandFormData.brandItems.trim()) {
      newErrors.brandItems = "Itens da marca são obrigatórios";
    }

    // Validação de Instagram (obrigatório)
    if (!brandFormData.instagram || !brandFormData.instagram.trim()) {
      newErrors.instagram = "Instagram é obrigatório";
    } else if (!brandFormData.instagram.startsWith("@")) {
      newErrors.instagram = "Instagram deve começar com @";
    } else if (brandFormData.instagram.length > 30) {
      newErrors.instagram = "Instagram deve ter no máximo 30 caracteres";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    setErrors({});

    if (activeStep === 0 && !validateUserForm()) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (activeStep === 1 && !validateAddressForm()) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (activeStep === 2 && !validateBrandForm()) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setActiveStep((prev) => prev + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setErrors({});
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setError(null);

    // Validar todos os formulários antes de enviar
    const isUserFormValid = validateUserForm();
    const isAddressFormValid = validateAddressForm();
    const isBrandFormValid = validateBrandForm();

    if (!isUserFormValid || !isAddressFormValid || !isBrandFormValid) {
      if (!isUserFormValid) {
        setActiveStep(0);
      } else if (!isAddressFormValid) {
        setActiveStep(1);
      } else if (!isBrandFormValid) {
        setActiveStep(2);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
      setIsLoading(false);
      return;
    }

    try {
      // Preparar todos os dados do usuário para envio
      const completeUserData = {
        // Dados básicos
        name: userFormData.name,
        email: userFormData.email,
        password: userFormData.password,
        phone: "55" + userFormData.phone.replace(/\D/g, ""), // Adiciona DDI 55 para Evolution API

        // Dados pessoais
        birthday: userFormData.birthday,
        cpf: userFormData.cpf ? userFormData.cpf.replace(/\D/g, "") : null,
        cnpj: userFormData.cnpj ? userFormData.cnpj.replace(/\D/g, "") : null,

        // Endereço
        zipcode: userFormData.zipcode.replace(/\D/g, ""), // Remove formatação e garante máximo 8 caracteres
        city: userFormData.city,
        state: userFormData.state,
        district: userFormData.district,
        address: userFormData.address,
        address_number: userFormData.address_number,
        address_complement: userFormData.address_complement?.trim() || "",

        // Dados da marca - corrigindo nomes dos campos
        brand_name: brandFormData.brandName,
        brand_description: brandFormData.description,
        category_id: parseInt(brandFormData.category), // Convertendo para número
        brand_items: brandFormData.brandItems,
        instagram_url: brandFormData.instagram?.trim() || "",
      };

      // Cadastrar usuário com todos os dados
      const userResponse = await signupApiRequest(completeUserData);

      if (userResponse.ok) {
        const userData = await userResponse.json();
        login(userData.accessToken);
        loginToken(userData.accessToken);
        if (userData.user) {
          localStorage.setItem("userInfo", JSON.stringify(userData.user));
        }
        navigate("/dashboard/events", { replace: true });
      } else {
        const errorData = await userResponse.json();
        setError(errorData.message || "Erro ao cadastrar usuário");
      }
    } catch (err: any) {
      setError(err.message || "Erro inesperado");
    } finally {
      setIsLoading(false);
    }
  };

  const getStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <UserForm
            formData={userFormData}
            setFormData={setUserFormData}
            errors={errors}
          />
        );
      case 1:
        return (
          <AddressForm
            formData={userFormData}
            setFormData={setUserFormData}
            errors={errors}
          />
        );
      case 2:
        return (
          <BrandForm
            formData={brandFormData}
            setFormData={setBrandFormData}
            errors={errors}
            categories={categories}
          />
        );
      case 3:
        return (
          <ConfirmationStep
            userFormData={userFormData}
            brandFormData={brandFormData}
          />
        );
      default:
        return null;
    }
  };

  if (activeStep === 3) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md space-y-8">
            <div className="flex justify-center">
              <div className="w-60 h-60 rounded-full bg-[#24324D] p-4 flex items-center justify-center">
                <img
                  src={logo}
                  alt="Logo"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
              <CardContent className="p-4 md:p-6">
                {success ? (
                  <SuccessMessage />
                ) : (
                  <ErrorMessage message={error || undefined} />
                )}

                <div className="mt-6 text-center">
                  <Link to="/login">
                    <Button className="bg-[#24324D] hover:bg-[#1e2a3d] text-white">
                      Voltar para o login
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="p-2 sm:p-4 md:p-6">
        <Link to="/">
          <Button
            variant="ghost"
            className="text-gray-600 hover:text-gray-900 p-0"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Voltar
          </Button>
        </Link>
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center p-2 sm:p-4">
        <div className="w-full max-w-2xl space-y-8">
          {/* Logo */}
          <div className="flex justify-center">
            <div className="w-24 h-24 rounded-full bg-[#24324D] p-4 flex items-center justify-center">
              <img
                src={logo}
                alt="Logo"
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          {/* Progress Steps */}
          <div className="flex justify-center">
            <div className="flex items-center justify-center w-full max-w-4xl">
              {/* Desktop version */}
              <div className="hidden md:flex items-center space-x-4">
                {steps.map((step, index) => {
                  const Icon = step.icon;
                  const isActive = index === activeStep;
                  const isCompleted = index < activeStep;

                  return (
                    <div key={step.id} className="flex items-center">
                      <div className="flex flex-col items-center">
                        <div
                          className={`
                          flex items-center justify-center w-12 h-12 rounded-full border-2 transition-colors
                          ${
                            isActive
                              ? "border-[#24324D] bg-[#24324D] text-white"
                              : isCompleted
                                ? "border-[#24324D] bg-[#24324D] text-white"
                                : "border-gray-300 bg-white text-gray-400"
                          }
                        `}
                        >
                          <Icon className="w-6 h-6" />
                        </div>
                        <span
                          className={`mt-2 text-sm font-medium text-center max-w-20 ${
                            isActive || isCompleted
                              ? "text-[#24324D]"
                              : "text-gray-400"
                          }`}
                        >
                          {step.title}
                        </span>
                      </div>
                      {index < steps.length - 1 && (
                        <div
                          className={`
                           w-16 h-0.5 mx-4 transition-colors
                           ${isCompleted ? "bg-[#24324D]" : "bg-gray-300"}
                         `}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Mobile version */}
              <div className="flex md:hidden items-start justify-between w-full max-w-sm">
                {steps.map((step, index) => {
                  const Icon = step.icon;
                  const isActive = index === activeStep;
                  const isCompleted = index < activeStep;

                  return (
                    <div
                      key={step.id}
                      className="flex flex-col items-center flex-1"
                    >
                      <div
                        className={`
                         flex items-center justify-center w-8 h-8 rounded-full border-2 transition-colors mb-1
                         ${
                           isActive
                             ? "border-[#24324D] bg-[#24324D] text-white"
                             : isCompleted
                               ? "border-[#24324D] bg-[#24324D] text-white"
                               : "border-gray-300 bg-white text-gray-400"
                         }
                      `}
                      >
                        <Icon className="w-4 h-4" />
                      </div>
                      <span
                        className={`text-xs font-medium text-center leading-tight ${
                          isActive || isCompleted
                            ? "text-[#24324D]"
                            : "text-gray-400"
                        } break-words whitespace-normal px-1 max-w-[90px]`}
                      >
                        {step.title}
                      </span>
                      {index < steps.length - 1 && (
                        <div
                          className={`
                          absolute top-4 left-1/2 w-full h-0.5 transition-colors -z-10
                          ${isCompleted ? "bg-[#24324D]" : "bg-gray-300"}
                        `}
                          style={{
                            width: `calc(100% / ${steps.length} - 2rem)`,
                            transform: "translateX(1rem)",
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Form Card */}
          <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="text-center">
              <CardTitle className="text-3xl font-bold text-gray-900">
                {steps[activeStep].title}
              </CardTitle>
              <CardDescription className="text-gray-600 text-lg">
                {activeStep === 0 && "Preencha seus dados pessoais"}
                {activeStep === 1 && "Informe seu endereço"}
                {activeStep === 2 && "Conte-nos sobre sua marca"}
                {activeStep === 3 && "Revise e confirme suas informações"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {error && <ErrorMessage message={error} />}

              {getStepContent()}

              {/* Navigation buttons */}
              <div className="flex justify-between pt-4">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={activeStep === 0}
                  className="border-gray-200 text-gray-600 hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Voltar
                </Button>

                {activeStep < 2 ? (
                  <Button
                    onClick={handleNext}
                    className="bg-[#24324D] hover:bg-[#1e2a3d] text-white"
                  >
                    Próximo
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="bg-[#24324D] hover:bg-[#1e2a3d] text-white disabled:opacity-50"
                  >
                    {isLoading ? "Cadastrando..." : "Finalizar Cadastro"}
                  </Button>
                )}
              </div>

              {/* Login link */}
              <div className="text-center pt-4 border-t border-gray-200">
                <p className="text-base text-gray-600">
                  Já tem uma conta?{" "}
                  <Link
                    to="/login"
                    className="text-[#24324D] hover:text-[#1e2a3d] hover:underline transition-colors"
                  >
                    Faça login
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
